/**
 * **Pick a booking flow for a button** (C19, FR-J11 item 8, ADR-0037 §5;
 * C20, ADR-0041 §1 and waves.md Wave 20 I-3).
 *
 * A section's button may link to a booking flow — `ButtonsField` offers it as
 * one of the four link kinds, and this is the dialog behind it. It picks a
 * **live** flow and, optionally, one of the plans that flow offers, and hands
 * back the stored link:
 *
 *     {kind: "booking", flow_slug}             the visitor chooses
 *     {kind: "booking", flow_slug, plan_id}    that plan is chosen for them
 *
 * The service resolves it at render to `/book/<slug>[?plan=<id>]` (ADR-0040),
 * and drops the button when the flow is a draft or gone. A paused flow's
 * button is still drawn and its page shows the firm's *not taking bookings*
 * line — the booking page's to draw, not this picker's.
 *
 * **Which plans.** A flow's offer names its services, and for each its plans;
 * none named means every service shown on the website, and no plans named
 * means every active plan (I-2, Wave 20).
 *
 * Offered only to a reader who holds `booking:can_view`, because the list of
 * flows is `booking:can_view` at the API. Without it the button's other three
 * kinds are still there.
 */

import { Alert, Button, Dialog, Select } from '@fiveox/ui'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { DialogActions, FORM_GRID, FormRow } from '../../../components/page'
import { ApiError } from '../../../lib/api'
import { booking } from '../../../lib/api/booking'
import { type Service, services as servicesApi } from '../../../lib/api/services'

/**
 * Was: *is this `url` field a button's link?* Since C20 a section's buttons
 * are their own field kind (`buttons`), so no `url` field carries the booking
 * picker any more; kept so older imports still read what it answers.
 */
export function isButtonLink(name: string): boolean {
  return name === 'href' || name.endsWith('_href')
}

export interface PlanChoice {
  id: string
  label: string
}

/** What `planChoices` reads of a flow — I-2's `offer`, and nothing else. */
export interface OfferingFlow {
  slug?: string
  offer?: { services?: { service_id: string; plan_ids?: string[] | null }[] | null } | null
}

/** The plans a flow offers, as `Service — Plan`: its services (every
 *  site-visible one when it names none), and for each the plans it ticks
 *  (every active one when it ticks none) — I-2's `offer`. */
export function planChoices(flow: OfferingFlow, catalog: Service[]): PlanChoice[] {
  const named = flow.offer?.services ?? []
  const offered = named.length
    ? named.flatMap((item) => {
        const service = catalog.find((s) => s.id === item.service_id)
        return service ? [{ service, plans: item.plan_ids ?? [] }] : []
      })
    : catalog.filter((s) => s.site_visible).map((service) => ({ service, plans: [] as string[] }))
  return offered.flatMap(({ service, plans }) =>
    (service.plans ?? [])
      .filter((plan) => plan.status === 'active' && (plans.length === 0 || plans.includes(plan.id)))
      .map((plan) => ({ id: plan.id, label: `${service.name} — ${plan.name}` })),
  )
}

/** The path a booking link resolves to at render (ADR-0040) — for the preview line only. */
export function bookingHref(slug: string, planId?: string | null): string {
  return planId ? `/book/${slug}?plan=${encodeURIComponent(planId)}` : `/book/${slug}`
}

export interface BookingChoice {
  flow_slug: string
  plan_id?: string
}

/** The dialog: a live flow, and a plan if the firm wants one chosen. */
export function BookingLinkDialog({
  initial,
  onClose,
  onPick,
}: {
  initial: { slug: string; planId: string | null } | null
  onClose: () => void
  onPick: (choice: BookingChoice) => void
}) {
  const [flows, setFlows] = useState<{ id: string; slug: string; name: string; offer?: OfferingFlow['offer'] }[] | null>(
    null,
  )
  const [catalog, setCatalog] = useState<Service[]>([])
  const [error, setError] = useState<string | null>(null)
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [planId, setPlanId] = useState(initial?.planId ?? '')

  useEffect(() => {
    let live = true
    Promise.all([booking.flows(), servicesApi.list('active').catch(() => null)])
      .then(([list, services]) => {
        if (!live) return
        const open = list.data.filter((f) => f.status === 'live')
        setFlows(open)
        setCatalog(services?.data.services ?? [])
        setSlug((prev) => (open.some((f) => f.slug === prev) ? prev : (open[0]?.slug ?? '')))
      })
      .catch((err: unknown) => {
        if (!live) return
        setFlows([])
        setError(err instanceof ApiError ? err.message : 'Your booking flows could not be loaded.')
      })
    return () => {
      live = false
    }
  }, [])

  const flow = flows?.find((f) => f.slug === slug) ?? null
  const plans = flow ? planChoices(flow, catalog) : []
  const plan = plans.some((p) => p.id === planId) ? planId : ''

  return (
    <Dialog open onClose={onClose} title="Link to a booking flow" error={error}>
      {flows === null ? (
        <p className="text-muted-foreground text-sm">Loading your live flows…</p>
      ) : flows.length === 0 ? (
        !error && (
          <Alert tone="info">
            You have no live booking flow yet. Build one and set it live under{' '}
            <Link to="/site/booking" className="font-medium underline">
              Booking flows
            </Link>
            , then link to it here.
          </Alert>
        )
      ) : (
        <div className={FORM_GRID}>
          <FormRow label="Flow" htmlFor="booking-link-flow">
            <Select id="booking-link-flow" value={slug} onChange={(e) => setSlug(e.target.value)}>
              {flows.map((f) => (
                <option key={f.id} value={f.slug}>
                  {f.name}
                </option>
              ))}
            </Select>
          </FormRow>
          <FormRow label="Plan" htmlFor="booking-link-plan" optional>
            {plans.length === 0 ? (
              <p className="text-muted-foreground pt-2 text-sm">No plans to pick from.</p>
            ) : (
              <Select id="booking-link-plan" value={plan} onChange={(e) => setPlanId(e.target.value)}>
                <option value="">The visitor chooses</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </Select>
            )}
          </FormRow>
          <FormRow label="The link">
            <code className="text-muted-foreground block pt-2 text-xs break-all" data-booking-link-preview="">
              {slug ? bookingHref(slug, plan || null) : '—'}
            </code>
          </FormRow>
        </div>
      )}
      <DialogActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!slug || !flows?.length}
          onClick={() => onPick(plan ? { flow_slug: slug, plan_id: plan } : { flow_slug: slug })}
          data-booking-link-use=""
        >
          Use this flow
        </Button>
      </DialogActions>
    </Dialog>
  )
}
